import React, { useState, useCallback, useMemo, useEffect } from "react";
import "./GcPreconCommercial.scss";
import DocumentPanel from "./DocumentPanel";
import QAPanel from "./QAPanel";
import ProgressBar from "./ProgressBar";
import ProjectHeader from "./ProjectHeader";
import SubmitConfirmation from "./SubmitConfirmation";
import Button from "../../../Components/Common/Button/Button";
import { getPreconConfig } from "./preconConfig";
import useGetGcChecklist from "../../../hooks/useGetGcChecklist";
import type {
  DocState,
  QAAnswers,
  QAQuestion,
  SubfieldAnswers,
} from "./preconConfig.types";
import { uploadFileToS3 } from "./UploadFileToS3";
import { submitGcChecklist } from "../../../services/preconService";
import { useLocation } from "react-router-dom";
import PreconChecklistSkeleton from "./PreconChecklistSkeleton";
import useGetProject from "../../../hooks/useGetProject";
import useScrollToTop from "../../../hooks/useScrollToTop";
import NetworkError from "Components/Common/NetworkError/NetworkError";

// Strips non-numeric characters and parses to a number; returns 0 for empty/invalid.
function parseCost(value: string | undefined): number {
  if (!value) return 0;
  const n = parseFloat(String(value).replace(/[^0-9.]/g, ""));
  return isNaN(n) ? 0 : n;
}

function validateCostFields(
  qaAnswers: QAAnswers,
  qaQuestions: QAQuestion[],
): Record<string, string> {
  const errors: Record<string, string> = {};

  const findAnswer = (key: string): string => {
    const idx = qaQuestions.findIndex((q) => q.key === key);
    if (idx === -1) return "";
    const answer = qaAnswers[idx + 1];
    return typeof answer === "string" ? answer.trim() : "";
  };

  const totalStr = findAnswer("total_cost");
  const hardStr = findAnswer("hard_cost");
  const softStr = findAnswer("soft_cost");

  if (!totalStr || (!hardStr && !softStr)) return errors;

  const total = parseCost(totalStr);
  const hard = parseCost(hardStr);
  const soft = parseCost(softStr);

  if (hardStr && hard > total) {
    errors["hard_cost"] = "Hard Cost cannot be greater than Total Cost.";
  }
  if (hardStr && softStr && hard + soft > total) {
    errors["soft_cost"] = "Hard Cost + Soft Cost cannot exceed Total Cost.";
  }
  return errors;
}

interface GcPreconCommercialProps {
  onBackToDashboard: () => void;
  projectId?: string;
  projectType?: string;
  projectData?: any;
}

// Keys whose answers must be serialized as { currencyCode, amount } in the payload
const COST_KEYS = new Set(["total_cost", "hard_cost", "soft_cost"]);

const GcPreconCommercial = ({
  onBackToDashboard,
  projectId,
  projectType: projectTypeProp = "residential",
}: GcPreconCommercialProps) => {
  const localStorgeUserData = JSON.parse(localStorage.getItem("user") || "{}");
  const [docState, setDocState] = useState<DocState>({});
  const [qaAnswers, setQaAnswers] = useState<QAAnswers>({});
  const [uploadedDocFileNames, setUploadedDocFileNames] = useState<string[]>(
    [],
  );
  const [uploadedDocKeys, setUploadedDocKeys] = useState<
    Record<number, string>
  >({});

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [draft, setDraft] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [currentScreen, setCurrentScreen] = useState<"precon" | "submitted">(
    "precon",
  );
  useScrollToTop(currentScreen === "submitted" ? 1 : 0);
  const location = useLocation();
  const { preconType } =
    (location.state as { preconType?: string } | null) ?? {};
  const {
    checklist,
    loading: checklistLoading,
    error: checklistError,
  } = useGetGcChecklist(projectId);
  const { projectData: fetchedProject, loading: projectLoading } =
    useGetProject(projectId) as { projectData: any; loading: boolean };

  // Derive project type: checklist API → project API → navigate state prop → default
  const projectType: string =
    (checklist as { projectType?: string } | null)?.projectType ??
    (fetchedProject?.projectType as string | undefined) ??
    projectTypeProp ??
    "commercial";

  useEffect(() => {
    if (!checklist) return;
    const { itemsByKey } = checklist as {
      itemsByKey: Record<string, { type: string; value: string | null }>;
    };
    if (!itemsByKey || Object.keys(itemsByKey).length === 0) return;

    const newDocState: DocState = {};
    const newDocKeys: Record<number, string> = {};
    const newDocFileNames: string[] = [];
    window.scrollTo({ top: 0, behavior: "smooth" });

    docLabels.forEach((doc, idx) => {
      const n = idx + 1;
      const item = itemsByKey[doc.key];
      newDocFileNames[idx] = "";
      if (!item) return;
      if (item.value) {
        newDocState[n] = "uploaded";
        let parsedDoc: { key: string; fileName: string } | null = null;
        if (
          item.value &&
          typeof item.value === "object" &&
          !Array.isArray(item.value)
        ) {
          parsedDoc = item.value as { key: string; fileName: string };
        } else if (typeof item.value === "string") {
          try {
            const jsonStart = item.value.indexOf("{");
            const jsonStr =
              jsonStart >= 0 ? item.value.slice(jsonStart) : item.value;
            let parsed = JSON.parse(jsonStr);
            if (parsed && typeof parsed === "string")
              parsed = JSON.parse(parsed);
            if (
              parsed &&
              typeof parsed === "object" &&
              !Array.isArray(parsed)
            ) {
              parsedDoc = parsed;
            }
          } catch {
            console.log("JSON parse failed:", item.value);
          }
        }
        if (parsedDoc?.key && parsedDoc?.fileName) {
          newDocKeys[n] = parsedDoc.key;
          newDocFileNames[idx] = parsedDoc.fileName;
        } else {
          const rawValue = String(item.value);
          newDocKeys[n] = rawValue;
          const parts = rawValue.split("/");
          newDocFileNames[idx] = parts[parts.length - 1];
        }
      } else {
        newDocState[n] = "na";
      }
    });

    const newQaAnswers: QAAnswers = {};
    qaQuestions.forEach((q, idx) => {
      const n = idx + 1;
      const item = itemsByKey[q.key];
      if (item && item.value != null) {
        // Cost fields are stored as { currencyCode, amount } — restore the plain amount string
        if (
          COST_KEYS.has(q.key) &&
          typeof item.value === "object" &&
          !Array.isArray(item.value) &&
          "amount" in (item.value as object)
        ) {
          newQaAnswers[n] = (
            item.value as { currencyCode: string; amount: string }
          ).amount;
        } else {
          newQaAnswers[n] = item.value;
        }
      }
    });

    if (Object.keys(newDocState).length > 0) setDocState(newDocState);
    if (Object.keys(newDocKeys).length > 0) setUploadedDocKeys(newDocKeys);
    setUploadedDocFileNames(newDocFileNames);
    if (Object.keys(newQaAnswers).length > 0) setQaAnswers(newQaAnswers);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checklist]);

  const { docLabels, docFileNames, qaQuestions } = getPreconConfig(
    preconType || projectType,
  );
  useEffect(() => {
    if (checklist) return;
    setUploadedDocFileNames(docFileNames);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [docFileNames]);

  const TOTAL_DOCS = docLabels.length;
  const TOTAL_QA = qaQuestions.length;

  // Calculate progress — subfield answers count as answered if any subfield is filled
  const progress = useMemo(() => {
    let docDone = 0,
      docNA = 0;
    for (let i = 1; i <= TOTAL_DOCS; i++) {
      if (docState[i] === "uploaded") docDone++;
      else if (docState[i] === "na") docNA++;
    }
    const qaAnswered = Object.entries(qaAnswers).filter(([nStr, a]) => {
      if (!a) return false;
      if (typeof a === "object") {
        const question = qaQuestions[parseInt(nStr) - 1];
        if (question?.type === "subfields" && question.subfields) {
          // Only count as answered when a user-input subfield (no defaultValue) has a value
          return question.subfields.some(
            (sf) =>
              !sf.defaultValue &&
              typeof (a as SubfieldAnswers)[sf.key] === "string" &&
              ((a as SubfieldAnswers)[sf.key] as string).trim().length > 0,
          );
        }
        return Object.values(a as SubfieldAnswers).some(
          (v) => v && typeof v === "string" && v.trim().length > 0,
        );
      }
      return typeof a === "string" && a.trim().length > 0;
    }).length;
    const totalItems = TOTAL_DOCS + TOTAL_QA;
    const completed = docDone + docNA + qaAnswered;
    const pct = Math.round((completed / totalItems) * 100);

    const requiredQATotal = qaQuestions.filter((q) => !q.optional).length;
    const requiredQAAnswered = Object.entries(qaAnswers).filter(([nStr, a]) => {
      const question = qaQuestions[parseInt(nStr) - 1];
      if (!question || question.optional) return false;
      if (!a) return false;
      if (typeof a === "object") {
        if (question.type === "subfields" && question.subfields) {
          return question.subfields.some(
            (sf) =>
              !sf.defaultValue &&
              typeof (a as SubfieldAnswers)[sf.key] === "string" &&
              ((a as SubfieldAnswers)[sf.key] as string).trim().length > 0,
          );
        }
        return Object.values(a as SubfieldAnswers).some(
          (v) => v && typeof v === "string" && v.trim().length > 0,
        );
      }
      return typeof a === "string" && a.trim().length > 0;
    }).length;
    const isFormComplete =
      docDone + docNA === TOTAL_DOCS && requiredQAAnswered === requiredQATotal;

    return {
      percentage: pct,
      completed,
      docDone,
      docNA,
      qaAnswered,
      pending: totalItems - completed,
      isFormComplete,
    };
  }, [docState, qaAnswers, TOTAL_DOCS, TOTAL_QA, qaQuestions]);

  const handleUploadDoc = useCallback(async (n: number, file: File) => {
    const result: any = await uploadFileToS3(file);
    const s3Key: string =
      result?.key ??
      result?.Key ??
      result?.data?.key ??
      result?.data?.Key ??
      "";
    setDocState((prev) => ({ ...prev, [n]: "uploaded" }));
    setUploadedDocKeys((prev) => ({ ...prev, [n]: s3Key }));
    setUploadedDocFileNames((prev) => {
      const next = [...prev];
      next[n - 1] = file.name;
      return next;
    });
  }, []);

  const handleMarkNA = useCallback((n: number) => {
    setDocState((prev) => ({
      ...prev,
      [n]: prev[n] === "na" ? null : "na",
    }));
  }, []);

  const handleRemove = useCallback((n: number) => {
    setDocState((prev) => ({ ...prev, [n]: null }));
    setUploadedDocKeys((prev) => ({ ...prev, [n]: "" }));
    setUploadedDocFileNames((prev) => {
      const next = [...prev];
      next[n - 1] = "";
      return next;
    });
  }, []);

  const handleQAChange = useCallback(
    (n: number, value: string | SubfieldAnswers) => {
      setQaAnswers((prev) => ({ ...prev, [n]: value }));
      const key = qaQuestions[n - 1]?.key;
      if (key) {
        setFieldErrors((prev) => {
          if (!prev[key]) return prev;
          const next = { ...prev };
          delete next[key];
          return next;
        });
      }
    },
    [qaQuestions],
  );

  useEffect(() => {
    const costErrors = validateCostFields(qaAnswers, qaQuestions);
    setFieldErrors((prev) => {
      const next = { ...prev };
      delete next["hard_cost"];
      delete next["soft_cost"];
      return { ...next, ...costErrors };
    });
  }, [qaAnswers, qaQuestions]);

  const buildItems = () => {
    const items: {
      type: string;
      key: string;
      value: string | SubfieldAnswers | null;
    }[] = [];

    docLabels.forEach((doc, idx) => {
      const n = idx + 1;
      const status = docState[n];
      if (status === "uploaded") {
        items.push({
          type: "document",
          key: doc.key,

          value: {
            key: uploadedDocKeys[n],
            fileName: uploadedDocFileNames[idx],
          },
        });
      } else if (status === "na") {
        items.push({ type: "document", key: doc.key, value: null });
      }
    });

    qaQuestions.forEach((q, idx) => {
      const n = idx + 1;
      const answer = qaAnswers[n];
      if (!answer) return;
      const value =
        COST_KEYS.has(q.key) && typeof answer === "string"
          ? { currencyCode: "USD", amount: answer }
          : answer;
      items.push({ type: "question", key: q.key, value });
    });

    return items;
  };

  const handleSaveAndExit = async () => {
    if (!projectId) return;
    setDraft(true);
    setSubmitError("");
    await submitGcChecklist(projectId, { items: buildItems() }, false);
    setDraft(false);
    handleBackToDashboard();
  };

  const handleSubmit = async () => {
    if (!projectId) return;

    const costErrors = validateCostFields(qaAnswers, qaQuestions);
    if (Object.keys(costErrors).length > 0) {
      setFieldErrors(costErrors);
      return;
    }
    setFieldErrors({});

    setIsSubmitting(true);
    setSubmitError("");

    const { error } = await submitGcChecklist(
      projectId,
      { items: buildItems() },
      true,
    );
    error as any;

    setIsSubmitting(false);
    if (error) {
      setSubmitError(
        (error as Error).message || "Submission failed. Please try again.",
      );
      return;
    }
    setCurrentScreen("submitted");
  };

  const handleBackToDashboard = () => {
    if (onBackToDashboard) {
      onBackToDashboard();
    }
  };

  if (checklistLoading || projectLoading) {
    return <PreconChecklistSkeleton />;
  }

  if (checklistError) {
    return (
      <div className="gc-precon-commercial">
        <div className="precon-layout">
          <Button className="btn btnSecondary" onClick={handleBackToDashboard}>
            ← Back to Dashboard
          </Button>
          <div className="precon-error">
            <NetworkError />
          </div>
        </div>
      </div>
    );
  }

  if (currentScreen === "submitted") {
    return (
      <SubmitConfirmation
        docDone={progress.docDone}
        docNA={progress.docNA}
        qaAnswered={progress.qaAnswered}
        totalQA={TOTAL_QA}
        projectData={fetchedProject}
        projectType={projectType}
        onBackToDashboard={handleBackToDashboard}
        localStorgeUserData={`${localStorgeUserData?.firstName ?? ""} ${localStorgeUserData?.lastName ?? ""}`.trim()}
      />
    );
  }
  return (
    <div className="gc-precon-commercial">
      <div className="precon-layout">
        <div className="precon-header">
          <Button
            className="btn btnSecondary btn-sm"
            onClick={handleBackToDashboard}
          >
            ← Back to Dashboard
          </Button>
        </div>

        <ProjectHeader projectData={fetchedProject} />

        <ProgressBar
          percentage={progress.percentage}
          completed={progress.completed}
          docNA={progress.docNA}
          pending={progress.pending}
          totalDocs={TOTAL_DOCS}
          totalQA={TOTAL_QA}
        />

        <div className="two-col">
          <DocumentPanel
            docState={docState}
            docLabels={docLabels}
            docFileNames={uploadedDocFileNames}
            onUpload={handleUploadDoc}
            onMarkNA={handleMarkNA}
            onRemove={handleRemove}
            docCount={progress.docDone + progress.docNA}
            totalDocs={TOTAL_DOCS}
            localStorgeUserData={localStorgeUserData?.email}
          />

          <QAPanel
            qaQuestions={qaQuestions}
            qaAnswers={qaAnswers}
            onChange={handleQAChange}
            qaCount={progress.qaAnswered}
            totalQA={TOTAL_QA}
            fieldErrors={fieldErrors}
          />
        </div>

        <div className="bottom-bar">
          <div className="save-note">
            {submitError ? (
              <span className="submit-error">{submitError}</span>
            ) : (
              "Progress is automatically saved as you work."
            )}
          </div>
          <div style={{ display: "flex", gap: "10px" }}>
            <Button
              className="btn btnSecondary"
              onClick={handleSaveAndExit}
              disabled={isSubmitting || draft}
            >
              {draft ? (
                <>
                  <span className="btn-spinner" /> Saving
                </>
              ) : (
                " Save & Exit"
              )}
            </Button>
            <Button
              className="btn btn-success"
              onClick={handleSubmit}
              disabled={isSubmitting || draft || !progress.isFormComplete}
            >
              {isSubmitting ? (
                <>
                  <span className="btn-spinner" /> Submitting
                </>
              ) : (
                "Submit for Review →"
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GcPreconCommercial;
